import { EyeOff, Flag } from 'lucide-react'
import { FC, MouseEvent } from 'react'
import PreviewLabel from '../PreviewLabel'
import { cn } from '@wellesley/ui'
import useHover from 'hooks/useHover'

export type ContentOverlayProps = {
  blurOnly?: boolean
  isSmallMedia?: boolean
  isMediaHidden?: boolean
  toggleShowMedia?: (value: boolean) => void
  contentWarning?: string | null
  isRemoveButton?: boolean
}

const SensitiveContentOverlay: FC<ContentOverlayProps> = ({
  blurOnly = false,
  isSmallMedia = false,
  isMediaHidden,
  toggleShowMedia,
  contentWarning,
  isRemoveButton = false,
}) => {
  const toggleShow = (e: MouseEvent) => {
    e.stopPropagation()
    toggleShowMedia?.(!!isMediaHidden)
  }

  const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
    isMediaHidden && e.stopPropagation()
  }

  const [hoverRef, isHovered] = useHover<HTMLDivElement>({ delay: 300 })

  const canRenderShowButton = isMediaHidden && !blurOnly
  const canRenderHideButton = !isMediaHidden && isHovered && !blurOnly

  return (
    <div
      ref={hoverRef}
      onClick={handleWrapperClick}
      data-testid="sensitive-overlay"
      className={cn(
        'absolute top-0 z-[0] flex h-full w-full flex-col items-center justify-center rounded-md text-sm font-semibold text-white  ',
        isMediaHidden &&
          `z-[3] backdrop-blur-[35px] after:absolute after:size-full after:bg-[var(--alpha-black-20)] after:content-['']`,
      )}
    >
      {!blurOnly && (
        <div
          className={cn(
            'mb-s z-[1] text-center',
            !isMediaHidden && 'opacity-0',
          )}
        >
          <Flag size={24} className="mx-auto" />
          <p className="mt-xxs">Content warning {contentWarning && ':'}</p>
          {contentWarning}
        </div>
      )}
      {canRenderShowButton && (
        <PreviewLabel
          onClick={toggleShow}
          position="static"
          testId="show-button"
          className="animate-in fade-in-0 zoom-in-95 z-[1] min-w-[60px]"
        >
          Show
        </PreviewLabel>
      )}
      {canRenderHideButton && (
        <PreviewLabel
          onClick={toggleShow}
          position={
            !isRemoveButton
              ? 'top-right'
              : isSmallMedia
                ? 'top-right3'
                : 'top-right2'
          }
          testId="hide-button"
          className={cn(
            'animate-in fade-in-0 zoom-in-95 z-[1] flex items-center justify-center rounded-full text-sm font-semibold',
            isSmallMedia ? 'size-s' : 'p-xs h-s w-[45px]',
          )}
        >
          {isSmallMedia ? <EyeOff size={16} color="white" /> : 'Hide'}
        </PreviewLabel>
      )}
    </div>
  )
}

export default SensitiveContentOverlay
